import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Box,
  Container,
  Menu,
  MenuItem
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import HubIcon from '@mui/icons-material/Hub';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';

const mainNavItems = [
  { name: 'Home', path: 'home' },
  { name: 'About', path: 'about' },
  { name: 'Platform', path: 'platform' },
  { name: 'Solutions', path: 'solutions' },
  { name: 'Ministries', path: 'ministries' },
  { name: 'Use Cases', path: 'use-cases' },
];

const secondaryNavItems = [
  { name: 'Insights', path: 'insights' },
  { name: 'Partners', path: 'partners' },
  { name: 'Careers', path: 'careers' },
  { name: 'Technology', path: 'technology' },
  { name: 'Case Studies', path: 'case-studies' },
];

const allNavItems = [...mainNavItems, ...secondaryNavItems];

const Logo = ({ scrolled }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      cursor: 'pointer',
    }}
  >
    <Typography
      variant="h5"
      sx={{
        fontWeight: 900,
        color: scrolled ? '#0F172A' : '#FFFFFF',
        letterSpacing: '-1px',
        fontSize: '1.6rem',
        lineHeight: 1,
        mb: 0.5
      }}
    >
      Niti<Box component="span" sx={{ color: 'primary.main' }}>X</Box>
    </Typography>
    <Typography
      variant="caption"
      sx={{
        fontWeight: 600,
        color: scrolled ? 'text.secondary' : 'rgba(255,255,255,0.8)',
        letterSpacing: '0.2px',
        fontSize: '0.65rem',
        whiteSpace: 'nowrap',
        display: { xs: 'none', sm: 'block' }
      }}
    >
      AI & Data Intelligence for Governance
    </Typography>
  </Box>
);

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeSection, setActiveSection] = useState('home');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);

      const sections = allNavItems.map(item => document.getElementById(item.path));
      const scrollPosition = window.scrollY + 120;

      sections.forEach(section => {
        if (section && scrollPosition >= section.offsetTop && scrollPosition < section.offsetTop + section.offsetHeight) {
          setActiveSection(section.id);
        }
      });
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const scrollToSection = (id) => {
    const isHomePage = location.pathname === '/' || location.pathname === '/home';
    const isAdminPage = location.pathname === '/admin' || location.pathname === '/registered-users' || location.pathname === '/registeredUsers';

    if (!isHomePage && !isAdminPage) {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          const offset = 80;
          const bodyRect = document.body.getBoundingClientRect().top;
          const elementRect = element.getBoundingClientRect().top;
          const elementPosition = elementRect - bodyRect;
          const offsetPosition = elementPosition - offset;

          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    } else {
      const element = document.getElementById(id);
      if (element) {
        const offset = 80;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }
    setMobileOpen(false);
    handleMenuClose();
  };

  const shouldBeSolid = scrolled;

  const drawer = (
    <Box sx={{ p: 3, pt: 2, bgcolor: 'background.default', height: '100%', color: 'text.primary' }}>
      <IconButton
        onClick={handleDrawerToggle}
        sx={{ position: 'absolute', right: 16, top: 16, color: 'text.primary' }}
      >
        <CloseIcon />
      </IconButton>

      <Box sx={{ mt: 4, mb: 6 }} onClick={() => scrollToSection('home')}>
        <Logo scrolled={true} />
      </Box>

      <List>
        {allNavItems.map((item) => {
          const isActive = activeSection === item.path;
          return (
            <ListItem
              key={item.name}
              disablePadding
              sx={{
                mb: 1,
                bgcolor: isActive ? 'rgba(0, 124, 255, 0.08)' : 'transparent',
                borderRadius: '8px',
                borderLeft: isActive ? '4px solid #007CFF' : '4px solid transparent'
              }}
            >
              <Box
                onClick={() => scrollToSection(item.path)}
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'none',
                  color: isActive ? '#007CFF' : '#0F172A',
                  fontWeight: isActive ? 800 : 500,
                  fontSize: '1rem',
                  padding: '12px 16px',
                  display: 'block',
                  width: '100%'
                }}
              >
                <ListItemText primary={item.name} />
              </Box>
            </ListItem>
          );
        })}
      </List>
      <Button
        variant="contained"
        fullWidth
        sx={{ mt: 4, bgcolor: '#007CFF', color: '#fff', fontWeight: 800, borderRadius: '8px' }}
        onClick={() => scrollToSection('contact')}
      >
        Request Demo
      </Button>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        sx={{
          bgcolor: shouldBeSolid ? '#FFFFFF' : 'transparent',
          boxShadow: shouldBeSolid ? '0 10px 40px rgba(0,0,0,0.05)' : 'none',
          backdropFilter: shouldBeSolid ? 'blur(20px)' : 'none',
          transition: 'all 0.3s ease',
          py: scrolled ? 0.5 : 1,
          borderBottom: shouldBeSolid ? '1px solid rgba(0,0,0,0.05)' : 'none'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between', minHeight: scrolled ? 55 : 65 }}>
            <Box onClick={() => scrollToSection('home')}>
              <Logo scrolled={shouldBeSolid} />
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{ display: { xs: 'none', lg: 'flex' }, alignItems: 'center', gap: 1 }}>
              {mainNavItems.map((item) => {
                const isActive = activeSection === item.path;
                return (
                  <Box
                    key={item.name}
                    onClick={() => scrollToSection(item.path)}
                    sx={{
                      px: 2,
                      py: 1,
                      cursor: 'pointer',
                      borderRadius: '6px',
                      color: shouldBeSolid ? (isActive ? 'primary.main' : 'text.primary') : '#FFFFFF',
                      fontWeight: isActive ? 800 : 500,
                      fontSize: '0.85rem',
                      textDecoration: 'none',
                      transition: '0.3s',
                      '&:hover': { color: 'primary.main', bgcolor: shouldBeSolid ? 'rgba(0,124,255,0.05)' : 'rgba(255,255,255,0.1)' }
                    }}
                  >
                    {item.name}
                  </Box>
                );
              })}

              <Button
                endIcon={<KeyboardArrowDownIcon />}
                onClick={handleMenuOpen}
                sx={{
                  color: shouldBeSolid ? 'text.primary' : '#FFFFFF',
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '0.85rem',
                  '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
                }}
              >
                Explore More
              </Button>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: { mt: 1.5, minWidth: 200, borderRadius: 2, boxShadow: '0 10px 40px rgba(0,0,0,0.1)', border: '1px solid rgba(0,0,0,0.05)' }
                }}
              >
                {secondaryNavItems.map((item) => (
                  <MenuItem
                    key={item.name}
                    onClick={() => scrollToSection(item.path)}
                    sx={{ fontSize: '0.85rem', py: 1.5, fontWeight: 500, '&:hover': { color: 'primary.main' } }}
                  >
                    {item.name}
                  </MenuItem>
                ))}
              </Menu>

              <Button
                variant="contained"
                sx={{
                  ml: 2,
                  bgcolor: 'primary.main',
                  color: '#FFFFFF',
                  fontWeight: 800,
                  borderRadius: '8px',
                  px: 3,
                  boxShadow: '0 4px 14px rgba(0, 124, 255, 0.4)',
                  '&:hover': {
                    bgcolor: 'primary.dark',
                    boxShadow: '0 6px 20px rgba(0, 124, 255, 0.6)',
                    transform: 'translateY(-1px)'
                  }
                }}
                onClick={() => scrollToSection('contact')}
              >
                Request Demo
              </Button>
            </Box>

            <Box sx={{ display: { xs: 'flex', lg: 'none' }, gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                sx={{
                  borderRadius: '6px',
                  fontWeight: 800,
                  fontSize: '0.7rem',
                  bgcolor: 'primary.main',
                  color: '#FFFFFF',
                  boxShadow: '0 4px 10px rgba(0, 124, 255, 0.3)'
                }}
                onClick={() => scrollToSection('contact')}
              >
                Request Demo
              </Button>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ color: shouldBeSolid ? 'primary.main' : '#FFFFFF' }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', lg: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 280, border: 'none' },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Navbar;
